import React, { useState, useEffect, useCallback } from 'react';
import { payoutAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { isKRRole } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Landmark, Search, Filter, ChevronLeft, ChevronRight, Loader2,
  Plus, CheckCircle2, XCircle, Clock, History, ShieldCheck, Eye, X,
} from 'lucide-react';

const EFT_STATUSES = ['Pending Review', 'Approved', 'Rejected'];
const ACCOUNT_TYPES = ['Checking', 'Savings'];
const PAYMENT_METHOD_TYPES = ['Direct Deposit', 'Check'];

const STATUS_COLORS = {
  'Pending Review': 'bg-amber-100 text-amber-700',
  Approved: 'bg-emerald-100 text-emerald-700',
  Rejected: 'bg-red-100 text-red-700',
};

const STATUS_ICONS = {
  'Pending Review': <Clock size={12} />,
  Approved: <CheckCircle2 size={12} />,
  Rejected: <XCircle size={12} />,
};

export default function EFTPage() {
  const { user } = useAuth();
  const isAdmin = isKRRole(user);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ status: 'all', payee_type: 'all' });
  const [showFilters, setShowFilters] = useState(false);

  // Submit dialog
  const [submitOpen, setSubmitOpen] = useState(false);
  const [form, setForm] = useState({
    payee_type: 'Agent', payee_id: '', payment_method_type: 'Direct Deposit',
    account_holder_name: '', bank_name: '', account_type: 'Checking',
    routing_number: '', account_number: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Review dialog
  const [reviewItem, setReviewItem] = useState(null);
  const [reviewAction, setReviewAction] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);

  // History dialog
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPayee, setHistoryPayee] = useState(null);

  // Payee search for EFT
  const [eftPayeeSearch, setEftPayeeSearch] = useState('');
  const [eftPayeeResults, setEftPayeeResults] = useState([]);
  const [eftPayeeLoading, setEftPayeeLoading] = useState(false);
  const [showEftPayeeDropdown, setShowEftPayeeDropdown] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page, page_size: pageSize,
        status: filters.status !== 'all' ? filters.status : undefined,
        payee_type: filters.payee_type !== 'all' ? filters.payee_type : undefined,
      };
      const res = await payoutAPI.eft.list(params);
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch {
      toast.error('Failed to load EFT records');
    }
    setLoading(false);
  }, [page, pageSize, filters]);

  useEffect(() => { loadData(); }, [loadData]);

  // EFT payee search
  useEffect(() => {
    if (!eftPayeeSearch || eftPayeeSearch.length < 1) return;
    const timer = setTimeout(async () => {
      setEftPayeeLoading(true);
      try {
        const res = await payoutAPI.payees({ search: eftPayeeSearch, payee_type: form.payee_type });
        setEftPayeeResults(res.data || []);
        setShowEftPayeeDropdown(true);
      } catch { setEftPayeeResults([]); }
      setEftPayeeLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [eftPayeeSearch, form.payee_type]);

  const selectEftPayee = (p) => {
    setForm(f => ({
      ...f,
      payee_type: p.payee_type,
      payee_id: p.payee_id,
      account_holder_name: p.payee_name,
    }));
    setEftPayeeSearch(p.payee_name);
    setShowEftPayeeDropdown(false);
  };

  const clearEftPayee = () => {
    setForm(f => ({ ...f, payee_id: '', account_holder_name: '' }));
    setEftPayeeSearch('');
    setShowEftPayeeDropdown(false);
  };

  const handleSubmit = async () => {
    if (!form.payee_id || !form.payee_type) {
      toast.error('Payee Type and Payee ID are required');
      return;
    }
    setSubmitting(true);
    try {
      await payoutAPI.eft.submit(form);
      toast.success('EFT information submitted for review');
      setSubmitOpen(false);
      setForm({ payee_type: 'Agent', payee_id: '', payment_method_type: 'Direct Deposit', account_holder_name: '', bank_name: '', account_type: 'Checking', routing_number: '', account_number: '', notes: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    }
    setSubmitting(false);
  };

  const handleReview = async () => {
    if (!reviewAction) return;
    setReviewing(true);
    try {
      await payoutAPI.eft.review(reviewItem.eft_id, reviewAction, reviewNotes || undefined);
      toast.success(`EFT ${reviewAction === 'approve' ? 'approved' : 'rejected'}`);
      setReviewItem(null);
      setReviewAction('');
      setReviewNotes('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to review');
    }
    setReviewing(false);
  };

  const openHistory = async (payeeType, payeeId, payeeName) => {
    setHistoryPayee({ type: payeeType, id: payeeId, name: payeeName });
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const res = await payoutAPI.eft.history(payeeType, payeeId);
      setHistoryItems(res.data);
    } catch {
      toast.error('Failed to load history');
    }
    setHistoryLoading(false);
  };

  const totalPages = Math.ceil(total / pageSize);
  const activeFilterCount = Object.values(filters).filter(v => v !== 'all').length;
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="space-y-6" data-testid="eft-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider">Accounting</p>
          <h1 className="text-2xl font-heading font-bold text-slate-900">Payment Information (EFT)</h1>
          <p className="text-sm text-slate-500 mt-1">Manage banking and payment details for agents and agencies</p>
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => setSubmitOpen(true)} data-testid="submit-eft-btn">
          <Plus size={14} /> Submit Banking Info
        </Button>
      </div>

      {/* Search & Filters */}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="gap-1.5" data-testid="toggle-eft-filters">
          <Filter size={14} /> Filters
          {activeFilterCount > 0 && <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{activeFilterCount}</Badge>}
        </Button>
      </div>

      {showFilters && (
        <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200" data-testid="eft-filter-panel">
          <Select value={filters.status} onValueChange={v => { setFilters(f => ({ ...f, status: v })); setPage(1); }}>
            <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {EFT_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.payee_type} onValueChange={v => { setFilters(f => ({ ...f, payee_type: v })); setPage(1); }}>
            <SelectTrigger className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="Agent">Agent</SelectItem>
              <SelectItem value="Agency">Agency</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-slate-400" size={28} /></div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center" data-testid="eft-empty">
              <Landmark size={36} className="mx-auto mb-3 text-slate-300" />
              <p className="text-sm font-medium text-slate-500">No banking information submitted yet</p>
              <p className="text-xs text-slate-400 mt-1">Submit banking details for agents or agencies to enable direct deposits</p>
              <Button size="sm" variant="outline" className="mt-4 gap-1.5" onClick={() => setSubmitOpen(true)}>
                <Plus size={14} /> Submit Banking Info
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="eft-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs text-slate-500 uppercase">
                    <th className="px-4 py-3">Payee</th>
                    <th className="px-4 py-3">Method</th>
                    <th className="px-4 py-3">Bank</th>
                    <th className="px-4 py-3">Account</th>
                    <th className="px-4 py-3">Routing</th>
                    <th className="px-4 py-3">Version</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Current</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(r => (
                    <tr key={r.eft_id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-800">{r.account_holder_name || r.payee_id}</p>
                        <p className="text-xs text-slate-400">{r.payee_type} — {r.payee_id}</p>
                      </td>
                      <td className="px-4 py-3 text-sm">{r.payment_method_type}</td>
                      <td className="px-4 py-3 text-sm">{r.bank_name || '-'}</td>
                      <td className="px-4 py-3 font-mono text-xs">{r.account_number || '-'}</td>
                      <td className="px-4 py-3 font-mono text-xs">{r.routing_number || '-'}</td>
                      <td className="px-4 py-3 text-center">
                        <Badge variant="outline" className="text-xs">v{r.version}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`text-xs gap-1 ${STATUS_COLORS[r.status] || ''}`}>
                          {STATUS_ICONS[r.status]} {r.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {r.is_current ? <ShieldCheck size={16} className="text-emerald-500 inline" /> : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center gap-1 justify-end">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openHistory(r.payee_type, r.payee_id, r.account_holder_name || r.payee_id)}
                            data-testid={`eft-history-${r.eft_id}`} title="Version History">
                            <History size={14} />
                          </Button>
                          {isAdmin && r.status === 'Pending Review' && (
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-sky-600" onClick={() => { setReviewItem(r); setReviewAction(''); setReviewNotes(''); }}
                              data-testid={`eft-review-${r.eft_id}`} title="Review">
                              <Eye size={14} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm" data-testid="eft-pagination">
          <span className="text-slate-500">Page {page} of {totalPages} ({total} total)</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}><ChevronLeft size={14} /></Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}><ChevronRight size={14} /></Button>
          </div>
        </div>
      )}

      {/* Submit EFT Dialog */}
      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="submit-eft-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Submit Banking Information</DialogTitle>
            <DialogDescription>This will be submitted for admin review before activation</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Payee Type *</Label>
              <Select value={form.payee_type} onValueChange={v => { set('payee_type', v); clearEftPayee(); }}>
                <SelectTrigger className="h-9 text-sm" data-testid="eft-payee-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Agent">Agent</SelectItem>
                  <SelectItem value="Agency">Agency</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">
                {form.payee_type === 'Agent' ? 'Select Agent' : 'Select Agency'} *
              </Label>
              {form.payee_id ? (
                <div className="flex items-center gap-2 p-2 bg-sky-50 border border-sky-200 rounded-md" data-testid="eft-selected-payee">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">{form.account_holder_name}</p>
                    <p className="text-xs text-slate-500">{form.payee_type} | ID: {form.payee_id}</p>
                  </div>
                  <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={clearEftPayee}>
                    <X size={14} />
                  </Button>
                </div>
              ) : (
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input value={eftPayeeSearch}
                    onChange={e => { setEftPayeeSearch(e.target.value); if (!e.target.value) setShowEftPayeeDropdown(false); }}
                    onFocus={() => { if (eftPayeeResults.length) setShowEftPayeeDropdown(true); }}
                    placeholder={`Search ${form.payee_type.toLowerCase()} by name or ID...`}
                    className="pl-9 h-9 text-sm" data-testid="eft-payee-search" />
                  {eftPayeeLoading && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
                  {showEftPayeeDropdown && eftPayeeResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {eftPayeeResults.map((p, i) => (
                        <button key={i} type="button" onClick={() => selectEftPayee(p)}
                          className="w-full text-left px-3 py-2 hover:bg-sky-50 border-b border-slate-100 last:border-0 transition-colors"
                          data-testid={`eft-payee-option-${p.payee_id}`}>
                          <p className="text-sm font-medium text-slate-800">{p.payee_name}</p>
                          <p className="text-xs text-slate-400">{p.detail}{p.agency_name ? ` | ${p.agency_name}` : ''}</p>
                        </button>
                      ))}
                    </div>
                  )}
                  {showEftPayeeDropdown && eftPayeeSearch && !eftPayeeLoading && eftPayeeResults.length === 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg p-3 text-center">
                      <p className="text-xs text-slate-400">No {form.payee_type.toLowerCase()}s found</p>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Payment Method</Label>
                <Select value={form.payment_method_type} onValueChange={v => set('payment_method_type', v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid="eft-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHOD_TYPES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Account Type</Label>
                <Select value={form.account_type} onValueChange={v => set('account_type', v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid="eft-account-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACCOUNT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Bank Name</Label>
              <Input value={form.bank_name} onChange={e => set('bank_name', e.target.value)}
                placeholder="Bank name" className="h-9 text-sm" data-testid="eft-bank-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Routing Number</Label>
                <Input value={form.routing_number} onChange={e => set('routing_number', e.target.value)}
                  placeholder="9-digit routing" className="h-9 text-sm font-mono" data-testid="eft-routing" />
              </div>
              <div>
                <Label className="text-xs">Account Number</Label>
                <Input value={form.account_number} onChange={e => set('account_number', e.target.value)}
                  placeholder="Account number" className="h-9 text-sm font-mono" data-testid="eft-account-num" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={form.notes} onChange={e => set('notes', e.target.value)}
                placeholder="Additional notes..." className="text-sm" rows={2} data-testid="eft-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubmitOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} data-testid="confirm-submit-eft-btn">
              {submitting ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Submit for Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={!!reviewItem} onOpenChange={() => setReviewItem(null)}>
        <DialogContent className="max-w-sm" data-testid="review-eft-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Review EFT Submission</DialogTitle>
            <DialogDescription>Verify the banking details and approve or reject</DialogDescription>
          </DialogHeader>
          {reviewItem && (
            <div className="space-y-3">
              <div className="text-sm space-y-1 bg-slate-50 rounded-lg p-3 border">
                <p><span className="text-slate-500">Holder:</span> <strong>{reviewItem.account_holder_name}</strong></p>
                <p><span className="text-slate-500">Bank:</span> {reviewItem.bank_name}</p>
                <p><span className="text-slate-500">Type:</span> {reviewItem.account_type} — {reviewItem.payment_method_type}</p>
                <p><span className="text-slate-500">Routing:</span> <span className="font-mono">{reviewItem.routing_number}</span></p>
                <p><span className="text-slate-500">Account:</span> <span className="font-mono">{reviewItem.account_number}</span></p>
                <p><span className="text-slate-500">Version:</span> v{reviewItem.version}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className={`flex-1 gap-1.5 ${reviewAction === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}
                  onClick={() => setReviewAction('approve')} data-testid="review-approve-btn">
                  <CheckCircle2 size={14} /> Approve
                </Button>
                <Button size="sm" className={`flex-1 gap-1.5 ${reviewAction === 'reject' ? 'bg-red-600 hover:bg-red-700' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'}`}
                  onClick={() => setReviewAction('reject')} data-testid="review-reject-btn">
                  <XCircle size={14} /> Reject
                </Button>
              </div>
              <div>
                <Label className="text-xs">Review Notes</Label>
                <Textarea value={reviewNotes} onChange={e => setReviewNotes(e.target.value)}
                  placeholder="Optional notes..." className="text-sm" rows={2} data-testid="review-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewItem(null)}>Cancel</Button>
            <Button onClick={handleReview} disabled={reviewing || !reviewAction} data-testid="confirm-review-btn">
              {reviewing ? <Loader2 size={14} className="animate-spin mr-1" /> : null} Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="eft-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Version History{historyPayee ? ` — ${historyPayee.name}` : ''}</DialogTitle>
          </DialogHeader>
          {historyLoading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin text-slate-400" size={24} /></div>
          ) : historyItems.length === 0 ? (
            <p className="text-sm text-slate-400 py-4 text-center">No history found</p>
          ) : (
            <div className="space-y-3">
              {historyItems.map(h => (
                <div key={h.eft_id} className={`p-3 rounded-lg border ${h.is_current ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">v{h.version}</Badge>
                      <Badge className={`text-xs gap-1 ${STATUS_COLORS[h.status] || ''}`}>{STATUS_ICONS[h.status]} {h.status}</Badge>
                      {h.is_current && <Badge className="text-xs bg-emerald-600 text-white">Current</Badge>}
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 space-y-0.5 mt-2">
                    <p><span className="font-medium">Bank:</span> {h.bank_name} | {h.account_type} | {h.payment_method_type}</p>
                    <p><span className="font-medium">Routing:</span> {h.routing_number} | <span className="font-medium">Account:</span> {h.account_number}</p>
                    <p><span className="font-medium">Submitted:</span> {h.submitted_by} — {new Date(h.submitted_at).toLocaleDateString()}</p>
                    {h.reviewed_by && <p><span className="font-medium">Reviewed:</span> {h.reviewed_by} — {new Date(h.reviewed_at).toLocaleDateString()}</p>}
                    {h.review_notes && <p><span className="font-medium">Notes:</span> {h.review_notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
